import type { ReactNode } from "react";
import { Modal } from "../Modal";

/**
 * The details panel a register row opens: a headline amount and status, the
 * record's facts, any long text in full, then whatever can be done with it
 * (passed in as `children` and `footer`, since that depends on the page).
 */
export function DetailsModal({
  title,
  headline,
  status,
  facts,
  sections = [],
  notice,
  onClose,
  footer,
  children,
  size = "2xl",
}: {
  title: string;
  headline: { label: string; value: string; note?: string };
  status?: { label: string; dot: string };
  facts: [string, ReactNode][];
  /** Long text shown in full, e.g. notes or a cancellation reason; empty ones are skipped. */
  sections?: { title: string; body?: string | null; tone?: "plain" | "warning" | "danger" }[];
  /** A one-line callout under the headline. */
  notice?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children?: ReactNode;
  size?: "xl" | "2xl" | "3xl" | "4xl";
}) {
  const tones = {
    plain: "text-gray-800",
    warning: "rounded-md bg-amber-50 px-3 py-2 text-amber-900",
    danger: "rounded-md bg-red-50 px-3 py-2 text-red-800",
  };
  return (
    <Modal isOpen size={size} title={title} onClose={onClose} footer={footer ?? <button type="button" onClick={onClose} className="btn-outline">Close</button>}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg bg-primary-50 px-4 py-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-primary-700">{headline.label}</p>
            <p className="text-2xl font-semibold tabular-nums text-gray-900">{headline.value}</p>
            {headline.note && <p className="mt-0.5 text-sm text-gray-600">{headline.note}</p>}
          </div>
          {status && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
              <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
              {status.label}
            </span>
          )}
        </div>

        {notice}

        <dl className="grid gap-x-6 rounded-lg border border-gray-200 px-4 py-1 sm:grid-cols-2">
          {facts.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 border-b border-gray-100 py-2 last:border-0">
              <dt className="text-sm text-gray-500">{label}</dt>
              <dd className="break-words text-right text-sm font-medium text-gray-900">{value || "—"}</dd>
            </div>
          ))}
        </dl>

        {sections
          .filter((section) => section.body?.trim())
          .map((section) => (
            <section key={section.title}>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{section.title}</h4>
              <p className={`mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 ${tones[section.tone ?? "plain"]}`}>{section.body}</p>
            </section>
          ))}

        {children}
      </div>
    </Modal>
  );
}

/** A titled box inside a details panel holding one action's form. */
export function ActionPanel({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-gray-200 p-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
      </div>
      {children}
    </section>
  );
}
